/**
 * PureEatiz quick add — "Add to cart" buttons on pe-product-card.
 *
 * Mirrors Horizon's product-form flow so the cart drawer, cart bubble and cart
 * page all update: dispatch a CartLinesUpdateEvent (from @shopify/events, mapped in
 * snippets/scripts.liquid) whose promise resolves with the refreshed cart and the
 * re-rendered cart sections returned by /cart/add.js. The cart drawer auto-opens
 * (when enabled in theme settings) because it listens to that event.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const SELECTOR = '[data-pe-quick-add]';

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
    .filter(Boolean);
}

async function fetchCart() {
  const cartItems = /** @type {any} */ (document.querySelector('cart-items-component'));
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
  }
  const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  return response.json();
}

/** @param {HTMLButtonElement} button */
async function quickAdd(button) {
  if (button.getAttribute('aria-busy') === 'true') return;
  const variantId = button.dataset.peQuickAdd;
  if (!variantId) return;

  button.setAttribute('aria-busy', 'true');

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');
  const sectionIds = cartSectionIds();
  if (sectionIds.length) {
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
      credentials: 'same-origin',
    });
    const data = await response.json();
    const didError = Boolean(data.status);

    if (didError) {
      button.dispatchEvent(
        new CartErrorEvent({
          error: data.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: data.description, errors: data.errors },
        })
      );
    }

    const cart = await fetchCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError,
        items: cart.items,
        source: 'pe-quick-add',
        sourceId: variantId,
        itemCount: 1,
        productId: button.dataset.productId,
        sections: didError ? undefined : data.sections,
      },
    });

    if (didError && data.description) {
      button.title = data.description;
    }
  } catch (error) {
    deferred.reject(error);
    console.error('[pe-quick-add]', error);
  } finally {
    button.removeAttribute('aria-busy');
  }
}

if (!window.__peQuickAddBound) {
  window.__peQuickAddBound = true;
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target.closest(SELECTOR) : null;
    if (!(target instanceof HTMLButtonElement)) return;
    event.preventDefault();
    quickAdd(target);
  });
}
